import React, { FC, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import { useAudioPlayback } from 'src/context/AudioContext';
import {
  LearnablePronunciationData,
  LearnablePronunciationScore,
} from 'src/api/userDictionary/types';
import { useKeyboardShortcut } from 'src/hooks';
import { AudioValidationResult } from '../../utils/validateAudioBuffer';
import { useLearnableMedia } from '../../hooks/useLearnableMedia';
import { useSessionSpeakerClips } from '../../hooks/useSessionSpeakerClips';
import { SpeakerMosaic } from '../SpeakerMosaic';
import * as S from './styles';

const msgs = defineMessages({
  sayItBack: { id: 'pronunciation_session_say_it_back', defaultMessage: 'Say it back' },
  instruction: { id: 'pronunciationBuddy_testCard_description' },
  audioAltText: { id: 'learning_session_learnables_card_audio' },
  replayVideo: {
    id: 'pronunciation_session_replay_speaker',
    defaultMessage: 'Play the speaker again',
  },
  nextSpeaker: {
    id: 'pronunciation_session_next_speaker',
    defaultMessage: 'Show another speaker',
  },
  speakerCount: {
    id: 'pronunciation_session_speaker_count',
    defaultMessage: '{current} of {total}',
  },
  inactiveMicrophoneHint: { id: 'pronunciationBuddy_testCard_pressMicro_description_web' },
  activeMicrophoneHint: { id: 'pronunciationBuddy_testCard_releaseMicro_description_web' },
  analysing: { id: 'pronunciationBuddy_analysing_description' },
  tooShort: { id: 'pronunicationBuddy_messageTooShort_description' },
  noOrLowAudio: { id: 'pronunicationBuddy_noOrLowAudio_description' },
  scorePass: { id: 'pronunciationBuddy_positiveOutcome_description' },
  scoreAlmost: { id: 'pronunciationBuddy_mediumOutcome_description' },
  scoreFail: { id: 'pronunciationBuddy_badOutcome_description' },
  tryAgain: { id: 'pronunciationBuddy_endOfSession_goAgainCTA' },
  continue: { id: 'Continue' },
});

/** Decorative only: the clip's real amplitude is never decoded. */
const AUDIO_WAVEFORM_BARS = [
  0.3, 0.5, 0.75, 0.45, 0.9, 0.6, 1, 0.55, 0.8, 0.4, 0.65, 0.95, 0.5, 0.7, 0.35, 0.85, 0.45, 0.6,
  0.75, 0.4, 0.55, 0.9, 0.5, 0.3,
];

const resultMessage = (
  score: LearnablePronunciationScore | null,
  validationResult: AudioValidationResult,
) => {
  if (validationResult === 'too-short') return msgs.tooShort;
  if (validationResult === 'silent') return msgs.noOrLowAudio;
  if (!score) return msgs.analysing;
  if (score.result === 'pass') return msgs.scorePass;
  if (score.result === 'almost') return msgs.scoreAlmost;
  return msgs.scoreFail;
};

/**
 * SPIKE: full-bleed take on the session, where the speaker video is the screen
 * and everything else floats over it. The video stays mounted through
 * recording, analysis and the result, so the learner never leaves the shot.
 */
export const ImmersivePronunciationView: FC<{
  learnable: LearnablePronunciationData;
  progressIndicator?: React.ReactNode;
  isRecording: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  isAnalysing: boolean;
  score: LearnablePronunciationScore | null;
  validationResult: AudioValidationResult;
  onRetry: () => void;
  onContinue?: () => void;
  /** The session's other words, whose speakers become the backdrop for a word
   * that has no video of its own. */
  sessionLearnables?: LearnablePronunciationData[] | null;
  /** Ignores the looked-up videos so the audio-only fallback can be rendered
   * for a word that was filmed, for stories. */
  forceAudioOnly?: boolean;
  /** Overrides the looked-up speaker clip, for stories. */
  videoUrl?: string;
}> = ({
  learnable,
  progressIndicator,
  isRecording,
  startRecording,
  stopRecording,
  isAnalysing,
  score,
  validationResult,
  onRetry,
  onContinue,
  sessionLearnables,
  forceAudioOnly = false,
  videoUrl,
}) => {
  const intl = useIntl();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeSpeaker, setActiveSpeaker] = useState(0);
  const { videoUrls, isPending: isLoadingMedia } = useLearnableMedia(learnable.learnableIdKey, {
    enabled: !videoUrl,
  });
  const filmedSpeakers = videoUrl ? [videoUrl] : videoUrls;
  const videoSpeakers = forceAudioOnly ? [] : filmedSpeakers;
  // Some words were only ever recorded, never filmed. Their recordings become
  // the speakers, so the counter, the next-speaker button and the per-word
  // reset all behave the same either way.
  const knowsMedia = forceAudioOnly || !isLoadingMedia;
  const isAudioOnly = knowsMedia && !videoSpeakers.length && learnable.audios.length > 0;
  const speakerCount = isAudioOnly ? learnable.audios.length : videoSpeakers.length;
  // Each word loads its own speakers, so the index can outrun the new list
  // while it arrives. Wrapping keeps a clip on screen instead of a black stage.
  const activeIndex = speakerCount ? activeSpeaker % speakerCount : 0;
  const speakerClip = videoSpeakers[activeIndex];
  const audioSrc = isAudioOnly ? learnable.audios[activeIndex] : learnable.audios[0];
  const { play, stopAll, playing } = useAudioPlayback(audioSrc);
  const hasMultipleSpeakers = speakerCount > 1;
  // Shares the query keys the intro and end-of-session mosaics use, so the
  // backdrop costs no extra requests.
  const sessionSpeakerClips = useSessionSpeakerClips(sessionLearnables ?? []);

  const hasResult = isAnalysing || !!score || validationResult !== 'valid';
  const isPass = score?.result === 'pass';
  const microphoneHint = intl.formatMessage(
    isRecording ? msgs.activeMicrophoneHint : msgs.inactiveMicrophoneHint,
  );

  useEffect(() => {
    stopAll();
  }, [learnable, stopAll]);

  // Every word opens on its own first speaker rather than wherever the previous
  // word was left. Keyed on the id, since `stopAll` above changes identity
  // whenever the audio context loads a clip and would reset mid-word.
  useEffect(() => {
    setActiveSpeaker(0);
  }, [learnable.learnableIdKey]);

  // Play the speaker once, with sound, then leave the last frame up so the
  // learner has quiet time to record.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !speakerClip) return;

    const playWithSound = async () => {
      video.currentTime = 0;
      video.muted = false;
      try {
        await video.play();
      } catch {
        // Browsers refuse unmuted playback without a user gesture. Fall back
        // to a silent first play so the shot isn't frozen; the replay button
        // is a gesture, so it will get sound.
        video.muted = true;
        try {
          await video.play();
        } catch (error) {
          console.error('Could not play the speaker clip:', error);
        }
      }
    };

    void playWithSound();
  }, [speakerClip]);

  const playSpeaker = () => {
    const video = videoRef.current;
    /* istanbul ignore if: spike */
    if (!video) return;
    video.muted = false;
    video.currentTime = 0;
    void video.play();
  };

  const toggleRecording = () => {
    if (playing || hasResult) return;
    if (isRecording) {
      stopRecording();
      return;
    }
    // Stop the speaker so their voice isn't recorded over the learner's
    videoRef.current?.pause();
    startRecording();
  };

  useKeyboardShortcut('space', isPass && onContinue ? onContinue : toggleRecording);

  return (
    <S.Stage>
      {speakerClip && (
        <S.Video
          ref={videoRef}
          key={speakerClip}
          src={speakerClip}
          playsInline
          // Decode the opening frame up front, so a browser that refuses
          // autoplay shows the speaker instead of a black rectangle.
          preload="auto"
          data-testid="immersiveSpeakerVideo"
        />
      )}

      {isAudioOnly && (
        <>
          <SpeakerMosaic clips={sessionSpeakerClips} />
          <S.AudioOverlay>
            <S.AudioCard>
              <S.PlayButton
                type="button"
                onClick={playing ? stopAll : play}
                aria-label={intl.formatMessage(msgs.audioAltText)}
                disabled={!audioSrc || isRecording}
                data-testid="immersiveAudioPlayer"
              >
                <Icon name={playing ? 'pause' : 'playFilled'} />
              </S.PlayButton>
              <S.AudioWaveform aria-hidden>
                {Array.from({ length: AUDIO_WAVEFORM_BARS.length }, (_, i) => (
                  <S.AudioWaveformBar
                    key={i}
                    $scale={AUDIO_WAVEFORM_BARS[i]}
                    $index={i}
                    $isPlaying={playing}
                  />
                ))}
              </S.AudioWaveform>
            </S.AudioCard>
          </S.AudioOverlay>
        </>
      )}

      <S.TopScrim>
        {progressIndicator}
        <S.Eyebrow>{intl.formatMessage(msgs.sayItBack)}</S.Eyebrow>
        <S.TargetValue dir="auto">{learnable.mainTargetValue}</S.TargetValue>
        {learnable.secondaryTargetValue && (
          <S.Phonetic dir="auto">{learnable.secondaryTargetValue}</S.Phonetic>
        )}
        <S.SourceValue dir="auto">{learnable.sourceDefinitionValue}</S.SourceValue>
      </S.TopScrim>

      <S.BottomScrim>
        <S.RecordCluster>
          <S.SecondaryActions>
            {hasMultipleSpeakers && (
              <>
                <S.SpeakerCount>
                  {intl.formatMessage(msgs.speakerCount, {
                    current: activeIndex + 1,
                    total: speakerCount,
                  })}
                </S.SpeakerCount>
                <S.RoundButton
                  type="button"
                  onClick={() => setActiveSpeaker((activeIndex + 1) % speakerCount)}
                  aria-label={intl.formatMessage(msgs.nextSpeaker)}
                >
                  <Icon name="forward" />
                </S.RoundButton>
              </>
            )}
            <S.RoundButton
              type="button"
              onClick={playSpeaker}
              aria-label={intl.formatMessage(msgs.replayVideo)}
              disabled={!speakerClip}
            >
              <Icon name="replay" />
            </S.RoundButton>
            <S.RoundButton
              type="button"
              onClick={playing ? stopAll : play}
              aria-label={intl.formatMessage(msgs.audioAltText)}
              disabled={!audioSrc || isRecording}
            >
              <Icon name={playing ? 'wave' : 'speakerVolume3'} />
            </S.RoundButton>
          </S.SecondaryActions>
          {hasResult ? (
            <S.ResultCard role="status">
              <S.ResultHeading $isPass={isPass}>
                {intl.formatMessage(resultMessage(score, validationResult))}
                {isAnalysing && (
                  <S.Ellipsis aria-hidden data-testid="analysingEllipsis">
                    {[0, 1, 2].map(index => (
                      <S.Dot key={index} $index={index}>
                        .
                      </S.Dot>
                    ))}
                  </S.Ellipsis>
                )}
              </S.ResultHeading>
              {!isAnalysing && (
                <S.ResultActions>
                  <S.SecondaryCta type="button" onClick={onRetry}>
                    {intl.formatMessage(msgs.tryAgain)}
                  </S.SecondaryCta>
                  {isPass && (
                    <S.PrimaryCta type="button" onClick={onContinue}>
                      {intl.formatMessage(msgs.continue)}
                    </S.PrimaryCta>
                  )}
                </S.ResultActions>
              )}
            </S.ResultCard>
          ) : (
            <S.RecordRow>
              <S.RecordCopy>
                <S.MicrophoneHint>{microphoneHint}</S.MicrophoneHint>
                {/* Decorative only: the recorder exposes no amplitude data */}
                <S.Waveform aria-hidden $isRecording={isRecording}>
                  {Array.from({ length: 16 }, (_, i) => (
                    <S.WaveformBar key={i} $index={i} $isRecording={isRecording} />
                  ))}
                </S.Waveform>
              </S.RecordCopy>
              <S.MicrophoneButton
                type="button"
                $isRecording={isRecording}
                onClick={toggleRecording}
                aria-label={microphoneHint}
                disabled={playing}
              >
                <Icon name="microphoneOutline" />
              </S.MicrophoneButton>
            </S.RecordRow>
          )}
        </S.RecordCluster>
      </S.BottomScrim>
    </S.Stage>
  );
};
